'use client'

import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cm } from '~/utils/utils'

interface SegmentControlsProps extends React.ComponentProps<typeof TabsPrimitive.Root> {
  className?: string
}

interface SegmentControlsListProps extends React.ComponentProps<typeof TabsPrimitive.List> {
  className?: string
  variant?: 'gray' | 'white'
  activeStyle?: 'light' | 'dark'
  size?: 'default' | 'sm'
}

interface SegmentControlsTriggerProps extends React.ComponentProps<typeof TabsPrimitive.Trigger> {
  className?: string
}

interface SegmentControlsContentProps extends React.ComponentProps<typeof TabsPrimitive.Content> {
  className?: string
}

// Context to share sliding indicator state between List and Triggers
interface SliderContextValue {
  activeValue: string | undefined
  activeStyle: 'light' | 'dark'
  size: 'default' | 'sm'
}

const SliderContext = React.createContext<SliderContextValue | null>(null)

function SegmentControls({ className, ...props }: SegmentControlsProps) {
  return <TabsPrimitive.Root className={cm('flex flex-col', className)} {...props} />
}

function SegmentControlsList({
  className,
  variant = 'gray',
  activeStyle = 'light',
  size = 'default',
  children,
  ...props
}: SegmentControlsListProps) {
  const backgroundColors = {
    gray: 'bg-[#F5F5F5] dark:bg-tokens-neutral-bg-grey-2',
    white:
      'bg-white border border-gray-200 dark:bg-tokens-neutral-bg-white dark:border-tokens-neutral-border-2'
  }

  const listRef = React.useRef<HTMLDivElement>(null)
  const [sliderStyle, setSliderStyle] = React.useState<React.CSSProperties>({})
  const [activeValue, setActiveValue] = React.useState<string | undefined>()
  const [isInitialized, setIsInitialized] = React.useState(false)

  // Use offsetWidth/offsetHeight instead of getBoundingClientRect to avoid
  // CSS transform scaling issues (e.g., modal animate-enter at scale(0.9))
  const updateSlider = React.useCallback(
    (disableTransition = false) => {
      const list = listRef.current
      if (!list) return

      const activeButton = list.querySelector('[data-state="active"]') as HTMLButtonElement | null
      if (activeButton) {
        // offsetLeft/offsetWidth are unaffected by ancestor CSS transforms,
        // unlike getBoundingClientRect which returns scaled values
        const buttonWidth = activeButton.offsetWidth
        const buttonHeight = activeButton.offsetHeight
        const buttonOffsetLeft = activeButton.offsetLeft

        // Disable transition when tabs change (e.g., switching tasks)
        if (disableTransition) {
          setIsInitialized(false)
        }

        const listPaddingPx = size === 'sm' ? 2 : 4

        setSliderStyle({
          width: buttonWidth,
          height: buttonHeight,
          transform: `translateX(${buttonOffsetLeft - listPaddingPx}px)`,
          opacity: 1
        })
        setActiveValue(activeButton.getAttribute('data-value') || undefined)

        // Enable transitions after first render (or re-enable after reset)
        if (!isInitialized || disableTransition) {
          requestAnimationFrame(() => setIsInitialized(true))
        }
      }
    },
    [isInitialized]
  )

  // Update slider position when active tab changes or children change
  // useLayoutEffect ensures slider is positioned before browser paint (no flash)
  React.useLayoutEffect(() => {
    updateSlider()

    // Use MutationObserver to detect active state and child changes
    const observer = new MutationObserver(mutations => {
      // Check if this is a child list change (tabs added/removed)
      const hasChildChange = mutations.some(m => m.type === 'childList')
      updateSlider(hasChildChange)
    })
    if (listRef.current) {
      observer.observe(listRef.current, {
        attributes: true,
        subtree: true,
        attributeFilter: ['data-state'],
        childList: true // Watch for tab additions/removals
      })
    }

    // Also update on resize
    const handleResize = () => updateSlider(false)
    window.addEventListener('resize', handleResize)

    return () => {
      observer.disconnect()
      window.removeEventListener('resize', handleResize)
    }
  }, [updateSlider])

  const contextValue = React.useMemo(
    () => ({
      activeValue,
      activeStyle,
      size
    }),
    [activeValue, activeStyle, size]
  )

  const listSizeClasses = {
    default: 'h-[43px] rounded-[12px] p-[4px] gap-[4px]',
    sm: 'h-8 rounded-xl p-0.5 gap-0.5'
  }

  const sliderPositionClasses = {
    default: 'top-[4px] left-[4px] rounded-[12px]',
    sm: 'top-0.5 left-0.5 rounded-[10px]'
  }

  return (
    <SliderContext.Provider value={contextValue}>
      <TabsPrimitive.List
        ref={listRef}
        className={cm(
          'w-fit inline-flex items-center justify-center relative',
          listSizeClasses[size],
          backgroundColors[variant],
          className
        )}
        {...props}
      >
        {/* Sliding indicator */}
        <div
          className={cm(
            'absolute pointer-events-none',
            sliderPositionClasses[size],
            activeStyle === 'dark'
              ? 'bg-tokens-neutral-bg-action'
              : 'bg-white shadow-segment dark:bg-tokens-neutral-bg-grey-4 dark:shadow-none',
            isInitialized ? 'transition-all duration-200 ease-out' : ''
          )}
          style={sliderStyle}
        />
        {/* Tab triggers */}
        {children}
      </TabsPrimitive.List>
    </SliderContext.Provider>
  )
}

function SegmentControlsTrigger({ className, ...props }: SegmentControlsTriggerProps) {
  const context = React.useContext(SliderContext)
  if (!context) {
    throw new Error('SegmentControlsTrigger must be rendered within SegmentControlsList.')
  }
  const isDark = context.activeStyle === 'dark'
  const size = context.size
  const triggerSizeClasses = {
    default: 'h-[35px] w-[124px] rounded-[12px] px-6 text-base data-[state=active]:h-9',
    sm: 'h-7 min-w-[68px] rounded-[10px] px-3 text-sm'
  }

  return (
    <TabsPrimitive.Trigger
      className={cm(
        'inline-flex items-center justify-center font-medium leading-[22px] whitespace-nowrap disabled:pointer-events-none select-none disabled:opacity-50 relative z-10',
        triggerSizeClasses[size],
        isDark
          ? 'text-[rgba(0,0,0,0.88)] dark:text-tokens-neutral-text-secondary'
          : 'text-black/25 dark:text-tokens-neutral-text-tertiary',
        isDark
          ? 'data-[state=active]:text-tokens-neutral-text-white'
          : 'data-[state=active]:text-[rgba(0,0,0,0.88)] dark:data-[state=active]:text-tokens-neutral-text-primary',
        className
      )}
      {...props}
    />
  )
}

function SegmentControlsContent({ className, ...props }: SegmentControlsContentProps) {
  return <TabsPrimitive.Content className={cm('flex-1 outline-none', className)} {...props} />
}

export { SegmentControls, SegmentControlsList, SegmentControlsTrigger, SegmentControlsContent }
